import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { format } from 'date-fns'
import api, { getMatters } from '../services/api'
import ClientPicker from '../components/ClientPicker'
import { useAuth } from '../context/AuthContext'
import { useToast } from '../context/ToastContext'
import { useFormDraft } from '../hooks/useFormDraft'
import { usePendingDelete } from '../hooks/usePendingDelete'
import useBulkSelect from '../hooks/useBulkSelect'
import useTableSort, { SortIcon } from '../hooks/useTableSort'
import useFieldIds from '../hooks/useFieldIds'
import { toFirmWallClock, utcToLocalInput, localInputToUtc } from '../utils/eventTime'
import TypingIndicator, { notifyTyping } from '../components/TypingIndicator'
import BulkActionBar from '../components/BulkActionBar'
import EmptyState from '../components/EmptyState'
import HelpButton from '../components/HelpButton'


const COMM_TYPES = [
  { value: 'phone_call', label: 'Phone Call', icon: '📞' },
  { value: 'email', label: 'Email', icon: '📧' },
  { value: 'letter', label: 'Letter', icon: '✉️' },
  { value: 'text', label: 'Text Message', icon: '💬' },
  { value: 'in_person', label: 'In-Person Meeting', icon: '🤝' },
  { value: 'video_call', label: 'Video Call', icon: '📹' },
  { value: 'fax', label: 'Fax', icon: '📠' },
]

const commIcon = (type) => COMM_TYPES.find(t => t.value === type)?.icon || '📝'
const commLabel = (type) => COMM_TYPES.find(t => t.value === type)?.label || type

export default function Communications() {
  const { user } = useAuth()
  const toast = useToast()
  const isAdmin = user?.role === 'admin' || user?.permissions?.is_admin === true
  const [communications, setCommunications] = useState([])
  const [clients, setClients] = useState([])
  const [matters, setMatters] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [filterType, setFilterType] = useState('')
  const [filterDirection, setFilterDirection] = useState('')
  // Batch D-B: dedicated filter chip for "unreturned only" — the callback
  // queue view.  Persisted so a user who lives in the Callback queue view
  // doesn't have to re-check every time they navigate away.
  const [filterUnreturned, setFilterUnreturned] = useState(() => {
    try { return localStorage.getItem('lextrack_comm_filter_unreturned') === '1' } catch { return false }
  })
  useEffect(() => {
    try { localStorage.setItem('lextrack_comm_filter_unreturned', filterUnreturned ? '1' : '0') } catch {}
  }, [filterUnreturned])
  const [bulkBusy, setBulkBusy] = useState(false)
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const initialForm = {
    client_id: '', matter_id: '', comm_type: 'phone_call', direction: 'outgoing',
    subject: '', description: '', duration_minutes: '',
    comm_date: utcToLocalInput(new Date()),
    // Batch D-B: callback-tracking flag.  Only meaningful when direction=incoming.
    // Backend silently coerces to false when direction=outgoing.
    needs_response: false,
  }
  const [form, setForm] = useState(initialForm)
  const fid = useFieldIds()

  // Ship 7b F2.2 — consume ?client=X&new=1 (from ClientDetail's 'e'
  // keyboard shortcut, which navigates here to start a new comm for that
  // client).  Opens the form + pre-selects the client so the shortcut
  // actually lands the user in a useful state.  Falls through silently if
  // the client id isn't recognized — the picker will show empty.
  useEffect(() => {
    const wantsNew = searchParams.get('new') === '1'
    const preClient = searchParams.get('client')
    if (wantsNew) {
      setShowForm(true)
      if (preClient) setForm((f) => ({ ...f, client_id: preClient }))
      setSearchParams({}, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])
  // Auto-save the new-communication form draft so a browser crash or tab
  // close doesn't wipe a long description mid-edit.
  const pending = usePendingDelete()
  const sort = useTableSort('communications', { key: 'comm_date', dir: 'desc' })
  const commDraft = useFormDraft('lextrack_draft_comm_new', form, setForm, {
    enabled: showForm,
    initialForm,
    debounceMs: 2000,
  })

  useEffect(() => { fetchData() }, [])

  const fetchData = async () => {
    try {
      const [commsRes, clientsRes, mattersRes] = await Promise.all([
        api.get('/api/communications'),
        api.get('/api/clients'),
        getMatters({ slim: true })
      ])
      setCommunications(commsRes.data)
      setClients(clientsRes.data)
      setMatters(mattersRes.data)
    } catch (err) {
      setError(err.response?.data?.error || 'An error occurred')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      // L-Cx5: send null instead of empty string for matter_id so the
      // backend can distinguish "no matter selected" from a bogus zero id.
      // comm_date is a `datetime-local` value: a naive wall clock carrying
      // FIRM-local intent.  communications.comm_date stores UTC in a naive
      // column, and the API binds whatever we send verbatim, so convert here
      // or a 2:30 PM call is filed as 2:30 UTC (7-8h early).
      const payload = {
        ...form,
        matter_id: form.matter_id || null,
        comm_date: localInputToUtc(form.comm_date),
      }
      await api.post('/api/communications', payload)
      commDraft.clear()
      setForm({
        client_id: '', matter_id: '', comm_type: 'phone_call', direction: 'outgoing',
        subject: '', description: '', duration_minutes: '',
        comm_date: utcToLocalInput(new Date()),
        needs_response: false,
      })
      setShowForm(false)
      fetchData()
    } catch (err) {
      setError(err.response?.data?.error || 'An error occurred')
    }
  }

  const handleDelete = (id) => {
    const target = communications.find(c => c.id === id)
    if (!target) return
    pending.schedule({
      url: `/api/communications/${id}`,
      message: 'Communication deleted',
      onOptimisticRemove: () => setCommunications(prev => prev.filter(c => c.id !== id)),
      // Re-sort on restore so the row lands back in its correct chronological
      // slot instead of at the top of the list.
      onRestore: () => setCommunications(prev => [...prev, target].sort((a, b) => new Date(b.comm_date) - new Date(a.comm_date))),
      onDeleteError: (err) => setError(err.response?.data?.error || 'Error deleting communication'),
    })
  }

  const filteredMatters = form.client_id
    ? matters.filter(m => String(m.client_id) === String(form.client_id))
    : matters

  // Filter communications
  let filtered = communications
  if (search) {
    const q = search.toLowerCase()
    filtered = filtered.filter(c =>
      c.subject?.toLowerCase().includes(q) ||
      c.description?.toLowerCase().includes(q) ||
      c.matter_title?.toLowerCase().includes(q) ||
      `${c.first_name} ${c.last_name}`.toLowerCase().includes(q)
    )
  }
  if (filterType) filtered = filtered.filter(c => c.comm_type === filterType)
  if (filterDirection) filtered = filtered.filter(c => c.direction === filterDirection)
  // Batch D-B: "unreturned only" is the callback-queue view — everything
  // flagged needs_response that hasn't been marked returned yet.
  if (filterUnreturned) {
    filtered = filtered.filter(c => c.needs_response === true && !c.responded_at)
  }
  filtered = sort.apply(filtered, {
    comm_date: (c) => c.comm_date || '',
    client: (c) => `${c.client_last_name || ''} ${c.client_first_name || ''}`.toLowerCase(),
    matter: (c) => (c.matter_title || '').toLowerCase(),
    comm_type: (c) => c.comm_type || '',
    direction: (c) => c.direction || '',
    subject: (c) => (c.subject || '').toLowerCase(),
    duration: (c) => parseInt(c.duration_minutes || 0, 10),
  })

  // Only rows the caller can actually delete are selectable — owner-or-admin
  // matches the server-side gate in communications.js, so we hide the checkbox
  // for rows the API would reject.
  const canEditRow = (c) => isAdmin || (c && user && c.user_id === user.id)
  const selectableRows = filtered.filter(canEditRow)
  const select = useBulkSelect(selectableRows, { rowKey: 'id' })
  const hasFilters = Boolean(search || filterType || filterDirection)

  const handleClearFilters = () => {
    setSearch('')
    setFilterType('')
    setFilterDirection('')
  }

  // Batched bulk-delete: hide all selected rows immediately, show ONE summary
  // toast with an Undo button, then commit the DELETEs after the 5-second
  // window.  Undo restores the snapshotted rows and cancels the commit.
  const handleBulkDelete = () => {
    if (select.size === 0 || bulkBusy) return
    const ids = Array.from(select.selectedIds)
    const snapshot = communications.filter(c => ids.includes(c.id))
    if (snapshot.length === 0) return
    // Optimistic remove
    setCommunications(prev => prev.filter(c => !ids.includes(c.id)))
    select.clear()
    setBulkBusy(true)
    let undone = false
    const commit = async () => {
      if (undone) { setBulkBusy(false); return }
      // Fire deletes in parallel — per-row 403s from the server-side owner
      // check are already prevented by the checkbox gate above.  Any leftover
      // failures land in fetchData()'s reconciliation step.
      const results = await Promise.all(ids.map(id =>
        api.delete(`/api/communications/${id}`).then(() => null).catch(err => err)
      ))
      const failed = results.filter(Boolean).length
      if (failed > 0) {
        toast.error(`${failed} of ${ids.length} deletes failed — refreshing.`)
      }
      setBulkBusy(false)
      fetchData()
    }
    const timer = setTimeout(commit, 5000)
    toast.custom({
      type: 'custom',
      message: `Deleted ${snapshot.length} communication${snapshot.length === 1 ? '' : 's'}`,
      duration: 5000,
      actions: [{
        label: 'Undo',
        onClick: () => {
          if (undone) return
          undone = true
          clearTimeout(timer)
          setCommunications(prev => [...prev, ...snapshot]
            .sort((a, b) => new Date(b.comm_date) - new Date(a.comm_date)))
          setBulkBusy(false)
        }
      }]
    })
  }

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <p className="text-gray-500">Loading communications...</p>
    </div>
  )

  return (
    <div>
      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 mb-4 text-sm">{error}</div>}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Communications<HelpButton slug="communications" /></h2>
          <p className="text-sm text-gray-500 mt-1">{filtered.length} entries</p>
        </div>
        <button onClick={() => setShowForm(!showForm)}
          className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
          + New Communication
        </button>
      </div>

      {/* New Communication Form */}
      {showForm && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">Log Communication</h3>
          {commDraft.banner}
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label htmlFor={fid('client')} className="block text-sm font-medium text-gray-700 mb-1">Client</label>
              {/* Feedback #505 — type-ahead client picker; filter excludes inactive
                  unless the current form value points to one (so editing existing
                  records still resolves the value). */}
              <ClientPicker
                id={fid('client')}
                required
                clients={clients}
                filter={(c) => c.status !== 'inactive' || c.id === parseInt(form.client_id)}
                value={form.client_id}
                onChange={(newId) => setForm({...form, client_id: newId, matter_id: ''})}
              />
            </div>
            <div>
              <label htmlFor={fid('matter')} className="block text-sm font-medium text-gray-700 mb-1">Matter <span className="text-gray-400 text-xs">(optional)</span></label>
              <select id={fid('matter')} value={form.matter_id}
                onChange={e => setForm({...form, matter_id: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                <option value="">Select matter</option>
                {filteredMatters.map(m => (
                  <option key={m.id} value={m.id}>{m.title}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor={fid('type')} className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select id={fid('type')} value={form.comm_type}
                onChange={e => setForm({...form, comm_type: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                {COMM_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.icon} {t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor={fid('direction')} className="block text-sm font-medium text-gray-700 mb-1">Direction</label>
              <select id={fid('direction')} value={form.direction}
                onChange={e => setForm({
                  ...form,
                  direction: e.target.value,
                  // Batch D-B: switching to outgoing implicitly clears the
                  // needs_response flag (backend does the same server-side).
                  needs_response: e.target.value === 'incoming' ? form.needs_response : false,
                })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                <option value="outgoing">Outgoing</option>
                <option value="incoming">Incoming</option>
              </select>
              {/* Batch D-B: callback checkbox — only visible on incoming.
                  An outgoing message we SENT can't need a response from us. */}
              {form.direction === 'incoming' && (
                <label className="mt-2 flex items-center gap-2 text-xs text-amber-800 cursor-pointer">
                  <input type="checkbox"
                    checked={!!form.needs_response}
                    onChange={e => setForm({...form, needs_response: e.target.checked})} />
                  <span>⚠ Needs a callback — track in the queue until returned</span>
                </label>
              )}
            </div>
            <div>
              <label htmlFor={fid('date')} className="block text-sm font-medium text-gray-700 mb-1">Date & Time</label>
              <input id={fid('date')} type="datetime-local" required value={form.comm_date}
                max={utcToLocalInput(new Date())}
                onChange={e => setForm({...form, comm_date: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label htmlFor={fid('duration')} className="block text-sm font-medium text-gray-700 mb-1">Duration (minutes)</label>
              <input id={fid('duration')} type="number" min="0" value={form.duration_minutes}
                onChange={e => setForm({...form, duration_minutes: e.target.value})}
                placeholder="Optional"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="md:col-span-3">
              <label htmlFor={fid('subject')} className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
              <input id={fid('subject')} type="text" required value={form.subject}
                onChange={e => setForm({...form, subject: e.target.value})}
                placeholder="Brief subject line"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="md:col-span-3">
              <label htmlFor={fid('description')} className="block text-sm font-medium text-gray-700 mb-1">Description / Notes</label>
              {/* Typing indicator only fires when a matter is picked — the
                  WS event is matter-scoped so a client-only (no matter) comm
                  has no scoped audience to publish to. */}
              {form.matter_id && <TypingIndicator matterId={form.matter_id} kind="comm.typing" />}
              <textarea id={fid('description')} value={form.description}
                onChange={e => {
                  setForm({...form, description: e.target.value})
                  if (form.matter_id) notifyTyping('comm.typing', form.matter_id)
                }}
                rows={3} placeholder="Details of the communication..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="md:col-span-3 flex gap-3">
              <button type="submit"
                className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors">
                Log Communication
              </button>
              <button type="button" onClick={() => setShowForm(false)}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-2 rounded-lg text-sm font-medium transition-colors">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[200px]">
            <input type="text" placeholder="Search by subject, client, or matter..."
              value={search} onChange={e => setSearch(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <select value={filterType} onChange={e => setFilterType(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
            <option value="">All Types</option>
            {COMM_TYPES.map(t => (
              <option key={t.value} value={t.value}>{t.icon} {t.label}</option>
            ))}
          </select>
          <select value={filterDirection} onChange={e => setFilterDirection(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
            <option value="">All Directions</option>
            <option value="incoming">Incoming</option>
            <option value="outgoing">Outgoing</option>
          </select>
          {/* Batch D-B: unreturned-only chip — the callback queue filter. */}
          <button
            type="button"
            onClick={() => setFilterUnreturned(v => !v)}
            className={`px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
              filterUnreturned
                ? 'bg-amber-100 border-amber-300 text-amber-800'
                : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}
            title="Show only unreturned incoming messages (the callback queue)"
          >
            ⚠ Unreturned only
          </button>
        </div>
      </div>

      {/* Bulk action bar — appears when ≥1 selectable row is selected */}
      <BulkActionBar
        count={select.size}
        onClear={select.clear}
        tone="purple"
        busy={bulkBusy}
        actions={[
          {
            label: `Delete ${select.size}`,
            icon: '🗑️',
            variant: 'danger',
            onClick: handleBulkDelete,
          },
        ]}
      />

      {/* Communications List */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        {filtered.length === 0 ? (
          communications.length === 0 ? (
            <EmptyState
              icon="📞"
              title="No communications yet"
              description="Log every phone call, email, and meeting for a complete matter timeline."
              action={{ label: '+ Log Communication', onClick: () => setShowForm(true) }}
            />
          ) : (
            <EmptyState
              variant="filtered"
              icon="📞"
              title="No matches"
              description="Try clearing the search or changing type/direction."
              action={{ label: 'Clear filters', onClick: handleClearFilters }}
            />
          )
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="w-10 px-3 py-3">
                  <input
                    type="checkbox"
                    aria-label="Select all visible communications"
                    checked={selectableRows.length > 0 && select.isAllSelected}
                    onChange={(e) => select.toggleAll(selectableRows, e.target.checked)}
                    disabled={selectableRows.length === 0}
                  />
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700" onClick={() => sort.toggle('comm_date')}>Date <SortIcon dir={sort.key === 'comm_date' ? sort.dir : null} /></th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700" onClick={() => sort.toggle('comm_type')}>Type <SortIcon dir={sort.key === 'comm_type' ? sort.dir : null} /></th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700" onClick={() => sort.toggle('client')}>Client <SortIcon dir={sort.key === 'client' ? sort.dir : null} /></th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700" onClick={() => sort.toggle('matter')}>Matter <SortIcon dir={sort.key === 'matter' ? sort.dir : null} /></th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700" onClick={() => sort.toggle('subject')}>Subject <SortIcon dir={sort.key === 'subject' ? sort.dir : null} /></th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Logged By</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(comm => (
                <tr key={comm.id} className="hover:bg-gray-50">
                  <td className="w-10 px-3 py-3 align-top">
                    {canEditRow(comm) ? (
                      <input
                        type="checkbox"
                        aria-label={`Select communication ${comm.subject || comm.id}`}
                        checked={select.has(comm.id)}
                        onChange={() => select.toggle(comm.id)}
                      />
                    ) : (
                      <span className="inline-block w-4 h-4" aria-hidden="true" />
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                    {format(toFirmWallClock(comm.comm_date), 'MMM d, yyyy')}
                    <span className="text-gray-400 ml-1 text-xs">
                      {format(toFirmWallClock(comm.comm_date), 'h:mm a')}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <span>{commIcon(comm.comm_type)}</span>
                      <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                        comm.direction === 'incoming' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                      }`}>
                        {comm.direction === 'incoming' ? '← In' : '→ Out'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5">{commLabel(comm.comm_type)}</p>
                    {comm.duration_minutes && (
                      <p className="text-xs text-gray-400">⏱ {comm.duration_minutes} min</p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-700 font-medium whitespace-nowrap">
                    {comm.first_name} {comm.last_name}
                  </td>
                  <td className="px-4 py-3">
                    {comm.matter_id ? (
                      <button onClick={() => navigate(`/matters/${comm.matter_id}`)}
                        className="text-primary-600 hover:underline text-left">
                        {comm.matter_title}
                      </button>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-700 max-w-xs">
                    <p className="font-medium truncate">{comm.subject}</p>
                    {comm.description && (
                      <p className="text-xs text-gray-400 truncate mt-0.5" title={comm.description}>{comm.description}</p>
                    )}
                    {/* Batch D-B: needs-callback chip.  Amber = open callback;
                        emerald with timestamp = resolved. */}
                    {comm.needs_response && !comm.responded_at && (
                      <p className="text-xs mt-1 inline-block bg-amber-100 text-amber-800 border border-amber-200 rounded-full px-2 py-0.5 font-medium">
                        ⚠ Needs callback
                      </p>
                    )}
                    {comm.needs_response && comm.responded_at && (
                      <p className="text-xs mt-1 inline-block bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5"
                         title={`Returned ${format(toFirmWallClock(comm.responded_at), 'MMM d, yyyy h:mm a')}`}>
                        ✓ Returned {format(toFirmWallClock(comm.responded_at), 'MMM d')}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                    {comm.logged_by_name}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {/* Batch D-B: Mark returned action — only shown when open */}
                    {comm.needs_response && !comm.responded_at && canEditRow(comm) && (
                      <button
                        onClick={async () => {
                          try {
                            const res = await api.put(`/api/communications/${comm.id}/mark-returned`)
                            setCommunications(prev => prev.map(x => x.id === comm.id ? { ...x, ...res.data } : x))
                          } catch (err) { setError(err.response?.data?.error || 'Could not mark returned') }
                        }}
                        className="text-xs text-emerald-600 hover:text-emerald-700 hover:underline mr-3"
                        title="Mark this callback as returned"
                      >
                        ✓ Mark returned
                      </button>
                    )}
                    {canEditRow(comm) && (
                      <button onClick={() => handleDelete(comm.id)}
                        className="text-xs text-red-400 hover:text-red-600 hover:underline">
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  )
}
